// frontend/src/pages/Register.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { vendorRegister } from '../api/auth.api.js';
import { FormField, ErrorMessage } from '../components/ui';

export default function Register() {
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    companyName: '', picName: '', picPhone: '', npwp: '', address: '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const { login } = useAuth();
  const navigate  = useNavigate();

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (form.password !== form.confirmPassword) {
      setError({ message: 'Passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      const data = await vendorRegister({
        email:       form.email,
        password:    form.password,
        companyName: form.companyName,
        picName:     form.picName,
        picPhone:    form.picPhone || undefined,
        npwp:        form.npwp    || undefined,
        address:     form.address || undefined,
      });
      login(data.accessToken, data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Rhaya Flicks</p>
          <h1 className="mt-1 text-3xl font-bold text-brand-700">Vendor Registration</h1>
          <p className="mt-2 text-sm text-gray-500">
            Register your company to submit agreements with Rhaya Flicks.
          </p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="pb-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Company Information</p>
            </div>

            <FormField label="Company Name" required>
              <input className="input" value={form.companyName} onChange={set('companyName')} required />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="PIC Name" required>
                <input className="input" value={form.picName} onChange={set('picName')} required />
              </FormField>
              <FormField label="PIC Phone">
                <input className="input" type="tel" value={form.picPhone} onChange={set('picPhone')} />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="NPWP">
                <input className="input" value={form.npwp} onChange={set('npwp')} />
              </FormField>
              <FormField label="Address">
                <input className="input" value={form.address} onChange={set('address')} />
              </FormField>
            </div>

            <div className="pt-2 pb-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Login Credentials</p>
            </div>

            <FormField label="Email" required>
              <input className="input" type="email" value={form.email} onChange={set('email')} required />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Password" required>
                <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={8} />
              </FormField>
              <FormField label="Confirm Password" required>
                <input className="input" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required />
              </FormField>
            </div>

            {error && <ErrorMessage error={error} />}

            <button type="submit" disabled={loading} className="btn-primary w-full mt-2">
              {loading ? 'Registering...' : 'Create Account'}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-500 hover:underline font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
